import React from 'react';
import { Rnd } from 'react-rnd';
import { Tooltip } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FontSizeOutlined,
  MailOutlined,
  UserOutlined,
  CalendarOutlined,
  FormOutlined,
  LockOutlined,
} from '@ant-design/icons';

// Field type icons
const FIELD_ICONS = {
  SIGNATURE: <EditOutlined style={{ fontSize: 14 }} />,
  TEXT: <FontSizeOutlined style={{ fontSize: 14 }} />,
  EMAIL: <MailOutlined style={{ fontSize: 14 }} />,
  NAME: <UserOutlined style={{ fontSize: 14 }} />,
  DATE: <CalendarOutlined style={{ fontSize: 14 }} />,
  INITIALS: <FormOutlined style={{ fontSize: 14 }} />,
};

const FieldItem = ({
  field,
  scaleFactor,
  onUpdate,
  onRemove,
  onClick,
  isActive,
  isEditable,
  showDelete,
  color,
  initials,
  currentRecipientId,
  allowFieldSelect,
}) => {
  // Calculate screen coordinates from PDF coordinates
  const screenX = field.position_x * scaleFactor;
  const screenY = field.position_y * scaleFactor;
  const screenWidth = field.width * scaleFactor;
  const screenHeight = field.height * scaleFactor;

  // Check if field is locked (e.g., already signed by another recipient)
  const isLocked = field.isLocked || (field.filled && currentRecipientId && field.recipient_id !== currentRecipientId);

  // Check if field belongs to current recipient
  const isOwnField = !currentRecipientId || field.recipient_id === currentRecipientId;

  // Determine if field has content
  const hasContent = field.type === 'SIGNATURE'
    ? field.signatureData && field.signatureData.trim() !== ''
    : field.custom_text && field.custom_text.trim() !== '';

  // Get border style based on state
  const getBorderStyle = () => {
    if (isLocked) return `2px solid #722ed1`; // Purple for locked
    if (isActive) return `2px solid #1890ff`; // Blue for active
    if (hasContent) return `2px solid #52c41a`; // Green for filled
    return `2px dashed ${color || '#999'}`; // Dashed with recipient color for empty
  };

  // Get background style
  const getBackgroundStyle = () => {
    if (hasContent) return 'rgba(255, 255, 255, 0.95)';
    return `${color}15`; // Slight tint of recipient color
  };

  // Handle field removal
  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove && !isLocked) {
      onRemove(field.id, field.recipient_id);
    }
  };

  // Handle field click
  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick && (isOwnField || allowFieldSelect) && !isLocked) {
      onClick();
    }
  };

  // Display content based on field type
  const renderContent = () => {
    if (field.type === 'SIGNATURE') {
      if (field.signatureData) {
        return (
          <img
            src={field.signatureData}
            alt="Signature"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              pointerEvents: 'none',
            }}
          />
        );
      }
    } else if (hasContent) {
      return (
        <span
          style={{
            fontSize: Math.min(screenHeight * 0.5, 14),
            color: '#333',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {field.custom_text}
        </span>
      );
    }

    // Empty field placeholder
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        {FIELD_ICONS[field.type]}
        <span style={{ fontSize: 10, color: '#666' }}>
          {field.type}
        </span>
      </div>
    );
  };

  return (
    <Rnd
      bounds="parent"
      size={{ width: screenWidth, height: screenHeight }}
      position={{ x: screenX, y: screenY }}
      disableDragging={!isEditable || isLocked}
      enableResizing={isEditable && !isLocked}
      onDragStop={(e, d) => {
        if (isEditable && !isLocked && onUpdate) {
          onUpdate({
            ...field,
            position_x: d.x / scaleFactor,
            position_y: d.y / scaleFactor,
          });
        }
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        if (isEditable && !isLocked && onUpdate) {
          onUpdate({
            ...field,
            position_x: position.x / scaleFactor,
            position_y: position.y / scaleFactor,
            width: ref.offsetWidth / scaleFactor,
            height: ref.offsetHeight / scaleFactor,
          });
        }
      }}
      onClick={handleClick}
      style={{
        border: getBorderStyle(),
        borderRadius: 4,
        background: getBackgroundStyle(),
        cursor: isLocked ? 'default' : isEditable ? 'move' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        pointerEvents: 'auto',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        boxShadow: isActive ? '0 0 0 2px rgba(24, 144, 255, 0.3)' : 'none',
      }}
    >
      {renderContent()}

      {/* Recipient initials badge */}
      <Tooltip title={`Assigned to recipient`}>
        <div
          style={{
            position: 'absolute',
            top: -8,
            left: -8,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: color || '#666',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 600,
            zIndex: 5,
          }}
        >
          {initials}
        </div>
      </Tooltip>

      {/* Lock indicator for locked fields */}
      {isLocked && (
        <Tooltip title="This field is locked">
          <div
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: '#722ed1',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              zIndex: 10,
            }}
          >
            <LockOutlined />
          </div>
        </Tooltip>
      )}

      {/* Delete button for editable, non-locked fields */}
      {showDelete && isEditable && !isLocked && (
        <Tooltip title="Remove field">
          <button
            onClick={handleRemove}
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: 'none',
              background: '#ff4d4f',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              zIndex: 10,
            }}
          >
            <DeleteOutlined />
          </button>
        </Tooltip>
      )}
    </Rnd>
  );
};

export default FieldItem;
